/**
 * 蚂蚁线覆盖层（M2-07）。
 *
 * 边界检测与斜纹图案在 **GPU** 上算（src/gpu/shaders/ants），本类负责：
 * 蒙版 → r8 纹理（按引用缓存，选区不变不重传）、按当前视口渲染、readback
 * 到一张透明的 2D 画布，交给渲染桥叠加在文档上方。
 *
 * readback 上屏是过渡方案：GPU 合成器接管显示管线后，蚂蚁线成为
 * Render Graph 的一个输出节点，这层搬运就消失了。动画节拍（~7fps 的
 * 相位推进）远低于渲染帧率，每拍一次 readback 的带宽完全可接受。
 */
import { selectBackend } from '../gpu/select';
import type { GpuBackend, GpuProgram, GpuTexture } from '../gpu/backend';
import { antsShaderSource } from '../gpu/shaders/ants';
import type { SelectionMask } from '../engine/selection/mask';
import type { Size, Viewport } from './viewport';

export class AntsOverlay {
  #backend: GpuBackend | null = null;
  #program: GpuProgram | null = null;
  #maskTexture: GpuTexture | null = null;
  #maskFor: SelectionMask | null = null;
  #target: GpuTexture | null = null;
  #frame: HTMLCanvasElement | null = null;
  #frameReady = false;
  #busy = false;
  #phase = 0;

  /** 初始化 GPU 后端。没有 GPU 时返回 false，蚂蚁线整体静默关闭。 */
  async init(): Promise<boolean> {
    this.#backend = await selectBackend();
    if (this.#backend === null) return false;
    this.#program = this.#backend.createProgram({
      source: antsShaderSource(),
      targetFormat: 'rgba8unorm',
      label: 'marching-ants',
    });
    return true;
  }

  /**
   * 推进一拍：相位 +2px 并渲染。完成后新帧可经 {@link drawTo} 叠加。
   * 上一拍还没读回时跳过（readback 是异步的，堆积没有意义）。
   */
  async tick(
    mask: SelectionMask,
    viewport: Viewport,
    view: Size,
  ): Promise<boolean> {
    const backend = this.#backend;
    if (backend === null || this.#program === null || this.#busy) return false;
    if (view.width < 1 || view.height < 1) return false;
    if (
      mask.width > backend.capabilities.maxTextureSize ||
      mask.height > backend.capabilities.maxTextureSize
    ) {
      return false; // 超出纹理上限的巨型文档：无蚂蚁线，选区本身不受影响
    }

    this.#busy = true;
    try {
      this.#ensureMaskTexture(backend, mask);
      this.#ensureTarget(backend, view);
      this.#phase = (this.#phase + 2) % 8;

      backend.draw({
        program: this.#program,
        target: this.#target!,
        vertexCount: 3,
        clear: [0, 0, 0, 0],
        uniforms: {
          u_dash: 4,
          u_docH: mask.height,
          u_docW: mask.width,
          u_mask: this.#maskTexture!,
          u_phase: this.#phase,
          u_rot: viewport.rotation,
          u_scale: viewport.scale,
          u_tx: viewport.tx,
          u_ty: viewport.ty,
          u_viewH: view.height,
          u_viewW: view.width,
        },
      });
      const pixels = (await backend.readback(this.#target!)) as Uint8Array;

      if (this.#frame === null) this.#frame = document.createElement('canvas');
      this.#frame.width = view.width;
      this.#frame.height = view.height;
      const ctx = this.#frame.getContext('2d');
      if (ctx === null) return false;
      const image = new ImageData(view.width, view.height);
      image.data.set(pixels);
      ctx.putImageData(image, 0, 0);
      this.#frameReady = true;
      return true;
    } finally {
      this.#busy = false;
    }
  }

  /** 把最近一帧叠加到画布（CSS 像素坐标系）。没有可用帧时无操作。 */
  drawTo(ctx: CanvasRenderingContext2D): void {
    if (!this.#frameReady || this.#frame === null) return;
    ctx.drawImage(this.#frame, 0, 0);
  }

  /** 清掉已渲染的帧（取消选择时调用，否则残影留在画布上）。 */
  clear(): void {
    this.#frameReady = false;
  }

  #ensureMaskTexture(backend: GpuBackend, mask: SelectionMask): void {
    if (this.#maskFor === mask && this.#maskTexture !== null) return;
    if (
      this.#maskTexture === null ||
      this.#maskTexture.width !== mask.width ||
      this.#maskTexture.height !== mask.height
    ) {
      this.#maskTexture?.destroy();
      this.#maskTexture = backend.createTexture({
        width: mask.width,
        height: mask.height,
        format: 'r8unorm',
        renderTarget: true,
        label: 'ants-mask',
      });
    }
    // 蒙版展平成连续字节上传。这是 GPU 纹理的物理要求，属显示路径的
    // 过渡开销（合成器接管后按 tile 上传），不违反引擎侧的分块不变量
    const bytes = new Uint8Array(mask.width * mask.height);
    const row = new Uint8Array(mask.width);
    for (let y = 0; y < mask.height; y++) {
      mask.readRow(y, 0, mask.width, row);
      bytes.set(row, y * mask.width);
    }
    backend.writeTexture(this.#maskTexture, bytes);
    this.#maskFor = mask;
  }

  #ensureTarget(backend: GpuBackend, view: Size): void {
    if (
      this.#target !== null &&
      this.#target.width === view.width &&
      this.#target.height === view.height
    ) {
      return;
    }
    this.#target?.destroy();
    this.#target = backend.createTexture({
      width: view.width,
      height: view.height,
      format: 'rgba8unorm',
      renderTarget: true,
      label: 'ants-frame',
    });
  }
}
